// Admin accounting tab — aggregates every income source in the platform,
// shows gross/net/VAT, and exports a single all-in-one PDF report.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Download, FileText, Calculator, AlertTriangle, FileSpreadsheet, Plus, Users, RefreshCw, UserPlus, ChevronLeft, ChevronRight, ExternalLink, Briefcase, Target, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { adminAccountingSummary } from "@/lib/api/accounting.functions";
import {
  listHubspotContacts, upsertHubspotContact, syncPayersToHubspot,
  getHubspotContact, addHubspotContactNote, getContactLedgerActivity,
  listHubspotDeals, listHubspotPipelines, createHubspotDeal, updateHubspotDealStage,
  syncBrandsToHubspotCompanies, verifyAccountingCrmLinks,
} from "@/lib/api/hubspot-crm.functions";
import { site, companyRegLine } from "@/lib/site";

export const Route = createFileRoute("/_authenticated/portal/admin/accounting")({
  head: () => ({ meta: [{ title: "Admin · Accounting" }] }),
  component: AccountingPage,
});

const rZAR = (cents: number) =>
  `R${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthAgoISO() {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function AccountingPage() {
  const [tab, setTab] = useState<"ledger" | "crm">("ledger");
  const [from, setFrom] = useState(monthAgoISO());
  const [to, setTo] = useState(todayISO());
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "outstanding">("all");
  const [query, setQuery] = useState("");
  const summarize = useServerFn(adminAccountingSummary);

  const { data, isFetching, refetch, error, isError } = useQuery({
    queryKey: ["admin-accounting", from, to],
    queryFn: () => summarize({ data: { from, to } }),
    staleTime: 30_000,
  });

  const filteredRows = useMemo(() => {
    if (!data) return [] as any[];
    const q = query.trim().toLowerCase();
    const isPaid = (r: any) => !["outstanding", "unpaid", "sent", "draft", "overdue"].includes(String(r.status || "").toLowerCase());
    return (data.rows as any[]).filter((r) => {
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
      if (statusFilter === "paid" && !isPaid(r)) return false;
      if (statusFilter === "outstanding" && isPaid(r)) return false;
      if (!q) return true;
      const hay = [r.reference, r.description, r.user_email, r.client_name]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" | ");
      return hay.includes(q);
    });
  }, [data, sourceFilter, statusFilter, query]);


  const byMonthRows = useMemo(() => {
    if (!data) return [] as { ym: string; gross_cents: number; count: number }[];
    return Object.entries(data.by_month)
      .map(([ym, v]) => ({ ym, ...(v as any) }))
      .sort((a, b) => (a.ym < b.ym ? 1 : -1));
  }, [data]);

  function downloadCsv() {
    if (!data) return;
    const rows = filteredRows;
    const header = ["Date", "Source", "Method", "Reference", "Invoice Ref", "Description", "Client", "Gross (ZAR)", "VAT-in (ZAR)", "Net (ZAR)"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const gross = r.amount_cents / 100;
      const vat = (r.amount_cents * 0.15) / 1.15 / 100;
      const net = gross - vat;
      const esc = (s: any) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      lines.push([
        new Date(r.paid_at).toISOString().slice(0, 10),
        r.source,
        r.method,
        r.reference,
        (r as any).invoice_reference ?? r.reference,
        esc(r.description),
        esc(r.user_email ?? ""),
        gross.toFixed(2),
        vat.toFixed(2),
        net.toFixed(2),
      ].join(","));
    }
    lines.push("");
    lines.push(`,,,,,,Totals gross,${(data.totals.gross_cents / 100).toFixed(2)},${(data.totals.vat_cents / 100).toFixed(2)},${(data.totals.net_cents / 100).toFixed(2)}`);
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `accounting-${from}-to-${to}${sourceFilter !== "all" ? `-${sourceFilter}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    if (!data) return;
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = 50;

    // Header band
    doc.setFillColor(11, 31, 58);
    doc.rect(0, 0, pageW, 90, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(site.name, margin, 30);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(companyRegLine(), margin, 46);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Accounting summary report", margin, 66);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Range: ${from} → ${to}`, margin, 80);
    y = 120;

    doc.setTextColor(20, 20, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Totals", margin, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(`Transactions: ${data.totals.count}`, margin, y); y += 14;
    doc.text(`Gross income: ${rZAR(data.totals.gross_cents)}`, margin, y); y += 14;
    doc.text(`VAT (15% included): ${rZAR(data.totals.vat_cents)}`, margin, y); y += 14;
    doc.text(`Net income (ex-VAT): ${rZAR(data.totals.net_cents)}`, margin, y); y += 22;

    const writeKV = (title: string, obj: Record<string, { gross_cents: number; count: number }>) => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(title, margin, y); y += 14;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      for (const [k, v] of Object.entries(obj)) {
        if (y > 780) { doc.addPage(); y = 50; }
        doc.text(`${k}`, margin, y);
        doc.text(`${v.count}`, pageW - margin - 160, y, { align: "right" });
        doc.text(rZAR(v.gross_cents), pageW - margin, y, { align: "right" });
        y += 12;
      }
      y += 8;
    };
    writeKV("By source", data.by_source);
    writeKV("By payment method", data.by_method);

    // Transactions
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    if (y > 740) { doc.addPage(); y = 50; }
    doc.text("Transactions", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text("Date", margin, y);
    doc.text("Source", margin + 95, y);
    doc.text("Method", margin + 165, y);
    doc.text("Reference", margin + 220, y);
    doc.text("Amount", pageW - margin, y, { align: "right" });
    y += 12;
    doc.setDrawColor(180); doc.line(margin, y - 2, pageW - margin, y - 2);

    for (const r of filteredRows) {
      if (y > 800) { doc.addPage(); y = 50; }
      doc.text(new Date(r.paid_at).toLocaleDateString("en-ZA"), margin, y);
      doc.text(r.source, margin + 95, y);
      doc.text(r.method, margin + 165, y);
      doc.text(String(r.reference).slice(0, 28), margin + 220, y);
      doc.text(rZAR(r.amount_cents), pageW - margin, y, { align: "right" });
      y += 11;
    }

    const footerY = 820;
    doc.setFontSize(8); doc.setTextColor(120);
    doc.text(
      `${companyRegLine()} · ${site.address.line1}, ${site.address.line2}, ${site.address.postalCode} · ${site.email}`,
      margin,
      footerY,
    );
    doc.text(
      `Generated ${new Date().toLocaleString("en-ZA")} · VAT shown at 15% inclusive`,
      margin,
      footerY + 10,
    );
    doc.save(`accounting-${from}-to-${to}.pdf`);
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy">Accounting</h1>
          <p className="text-sm text-muted-foreground">
            All transactions, income, VAT and outstanding invoices — one place, SARS-friendly exports.
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {companyRegLine()}
          </p>
        </div>
      </header>

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setTab("ledger")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "ledger" ? "border-navy text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}
        >
          <FileText className="mr-1 inline h-4 w-4" /> Ledger & reports
        </button>
        <button
          onClick={() => setTab("crm")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "crm" ? "border-navy text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}
        >
          <Users className="mr-1 inline h-4 w-4" /> CRM (HubSpot)
        </button>
      </div>

      {tab === "crm" ? <CrmPanel /> : <LedgerPanel {...{ from, setFrom, to, setTo, sourceFilter, setSourceFilter, statusFilter, setStatusFilter, query, setQuery, data, isFetching, refetch, error, isError, filteredRows, byMonthRows, downloadCsv, downloadPdf }} />}
    </div>
  );
}

function LedgerPanel(props: any) {
  const { from, setFrom, to, setTo, sourceFilter, setSourceFilter, statusFilter, setStatusFilter, query, setQuery, data, isFetching, refetch, error, isError, filteredRows, byMonthRows, downloadCsv, downloadPdf } = props;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1"><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[140px]" /></div>
          <div className="grid gap-1"><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[140px]" /></div>
          <div className="grid gap-1">
            <Label className="text-xs">Source</Label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">All sources</option>
              {data ? Object.keys(data.by_source).map((s: string) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              )) : null}
            </select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="outstanding">Outstanding</option>
            </select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Search</Label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Client, invoice #, description…" className="h-9 w-[220px]" />
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={downloadCsv} disabled={!data}>
            <FileSpreadsheet className="mr-1 h-4 w-4" /> CSV
          </Button>
          <Button size="sm" onClick={downloadPdf} disabled={!data}>
            <Download className="mr-1 h-4 w-4" /> PDF
          </Button>
          <Link
            to="/portal/admin/manual-invoices"
            className="inline-flex h-9 items-center gap-1 rounded-md bg-gold px-3 text-sm font-semibold text-navy hover:bg-gold/90"
          >
            <Plus className="h-3.5 w-3.5" /> New invoice
          </Link>
      </div>



      {isError ? <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{(error as any)?.message}</div> : null}

      {!data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-5">
            <Card label="Gross income" value={rZAR(data.totals.gross_cents)} accent />
            <Card label="VAT (15% incl.)" value={rZAR(data.totals.vat_cents)} />
            <Card label="Net (ex-VAT)" value={rZAR(data.totals.net_cents)} />
            <Card label="Paid transactions" value={String(data.totals.count)} />
            <Card
              label={`Outstanding · ${data.totals.outstanding_count} inv.`}
              value={rZAR(data.totals.outstanding_cents)}
              warn
            />
          </div>

          {data.outstanding && data.outstanding.length > 0 ? (
            <Box title="Outstanding invoices" icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1">Reference</th>
                    <th className="px-2 py-1">Client</th>
                    <th className="px-2 py-1">Status</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.outstanding.map((r: any) => (
                    <tr key={r.reference} className="border-t border-border">
                      <td className="px-2 py-1 font-mono text-[11px]">{r.reference}</td>
                      <td className="px-2 py-1 text-xs">{r.description.replace(/^Manual invoice · /, "")}</td>
                      <td className="px-2 py-1 text-xs capitalize">{r.status}</td>
                      <td className="px-2 py-1 text-right font-medium">{rZAR(r.amount_cents)}</td>
                      <td className="px-2 py-1 text-right">
                        <OpenDealButton
                          defaults={{
                            dealname: `${r.description.replace(/^Manual invoice · /, "")} · ${r.reference}`,
                            amount: r.amount_cents / 100,
                            contact_email: r.user_email ?? "",
                            source_reference: r.reference,
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Box title="By source" icon={<FileText className="h-4 w-4" />}>
              <KV obj={data.by_source} />
            </Box>
            <Box title="By payment method" icon={<Calculator className="h-4 w-4" />}>
              <KV obj={data.by_method} />
            </Box>
          </div>

          <Box title="Monthly breakdown" icon={<Calculator className="h-4 w-4" />}>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr><th className="px-2 py-1">Month</th><th className="px-2 py-1 text-right">Count</th><th className="px-2 py-1 text-right">Gross</th></tr>
              </thead>
              <tbody>
                {byMonthRows.map((r: any) => (
                  <tr key={r.ym} className="border-t border-border">
                    <td className="px-2 py-1">{r.ym}</td>
                    <td className="px-2 py-1 text-right">{r.count}</td>
                    <td className="px-2 py-1 text-right font-medium">{rZAR(r.gross_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Box
            title={`Transactions (showing ${filteredRows.length}${sourceFilter !== "all" ? ` · ${sourceFilter}` : ""})`}
            icon={<FileText className="h-4 w-4" />}
          >
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="sticky top-0 bg-muted/70 text-left text-xs uppercase">
                  <tr>
                    <th className="px-2 py-1">Date</th>
                    <th className="px-2 py-1">Source</th>
                    <th className="px-2 py-1">Method</th>
                    <th className="px-2 py-1">Reference</th>
                    <th className="px-2 py-1">Invoice Ref</th>
                    <th className="px-2 py-1">Description</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r: any, i: number) => {
                    const canConvert = ["invoice", "subscription", "manual_invoice", "subscription_order"].some((k) => String(r.source).includes(k));
                    return (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1 text-xs">{new Date(r.paid_at).toLocaleDateString("en-ZA")}</td>
                      <td className="px-2 py-1 text-xs">{r.source}</td>
                      <td className="px-2 py-1 text-xs">{r.method}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{r.reference}</td>
                      <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">
                        {r.invoice_reference && r.invoice_reference !== r.reference
                          ? r.invoice_reference
                          : "—"}
                      </td>
                      <td className="px-2 py-1 text-xs">{r.description}</td>
                      <td className="px-2 py-1 text-right font-medium">{rZAR(r.amount_cents)}</td>
                      <td className="px-2 py-1 text-right">
                        {canConvert || r.user_email ? (
                          <OpenDealButton
                            defaults={{
                              dealname: `${r.description} · ${r.reference}`,
                              amount: r.amount_cents / 100,
                              contact_email: r.user_email ?? "",
                              source_reference: r.reference,
                            }}
                            variant="ghost"
                          />
                        ) : null}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Box>
        </>
      )}
    </div>
  );
}


function Card({ label, value, accent, warn }: { label: string; value: string; accent?: boolean; warn?: boolean }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        accent
          ? "border-navy bg-navy text-white"
          : warn
            ? "border-amber-400 bg-amber-50"
            : "border-border bg-card"
      }`}
    >
      <div className={`text-xs uppercase tracking-wide ${accent ? "text-white/70" : warn ? "text-amber-800" : "text-muted-foreground"}`}>{label}</div>
      <div className={`mt-1 font-display text-2xl ${warn ? "text-amber-900" : ""}`}>{value}</div>
    </div>
  );
}
function Box({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="mb-2 flex items-center gap-2 font-display text-base text-navy">{icon}{title}</h3>
      {children}
    </div>
  );
}
function KV({ obj }: { obj: Record<string, { gross_cents: number; count: number }> }) {
  const rows = Object.entries(obj).sort((a, b) => b[1].gross_cents - a[1].gross_cents);
  if (!rows.length) return <p className="text-xs text-muted-foreground">No data.</p>;
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-t border-border">
            <td className="px-2 py-1 capitalize">{k.replace(/_/g, " ")}</td>
            <td className="px-2 py-1 text-right text-xs text-muted-foreground">{v.count}</td>
            <td className="px-2 py-1 text-right font-medium">{rZAR(v.gross_cents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// HubSpot CRM panel — Contacts + Pipeline sub-tabs
// ---------------------------------------------------------------------------
function CrmPanel() {
  const [subtab, setSubtab] = useState<"contacts" | "pipeline" | "reconciliation" | "verification">("contacts");

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div>
          <h3 className="font-display text-lg text-navy">HubSpot CRM</h3>
          <p className="text-xs text-muted-foreground">
            Manage contacts, deals, and brand→company mapping. Reconciliation and verification screens confirm every ledger record links to the right CRM contact and company.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {[
          { id: "contacts", label: "Contacts", icon: Users },
          { id: "pipeline", label: "Deals pipeline", icon: Briefcase },
          { id: "reconciliation", label: "Reconciliation", icon: RefreshCw },
          { id: "verification", label: "Verification", icon: Target },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSubtab(id as any)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${subtab === id ? "border-navy text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}
          >
            <Icon className="mr-1 inline h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {subtab === "contacts" && <ContactsPanel />}
      {subtab === "pipeline" && <PipelinePanel />}
      {subtab === "reconciliation" && <ReconciliationPanel />}
      {subtab === "verification" && <VerificationPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reconciliation panel — merge customers + brand→company mapper, show stats
// ---------------------------------------------------------------------------
function ReconciliationPanel() {
  const syncCustomers = useServerFn(syncPayersToHubspot);
  const syncBrands = useServerFn(syncBrandsToHubspotCompanies);
  const [customerReport, setCustomerReport] = useState<any | null>(null);
  const [brandReport, setBrandReport] = useState<any | null>(null);

  const runCustomers = useMutation({
    mutationFn: () => syncCustomers({ data: {} }),
    onSuccess: (r: any) => { setCustomerReport(r); toast.success(`Merged ${r.total_payers} unique customers`); },
    onError: (e: any) => toast.error(e?.message ?? "Sync failed"),
  });
  const runBrands = useMutation({
    mutationFn: () => syncBrands({ data: {} }),
    onSuccess: (r: any) => { setBrandReport(r); toast.success(`Mapped ${r.brands_scanned} brands to HubSpot companies`); },
    onError: (e: any) => toast.error(e?.message ?? "Brand mapping failed"),
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-navy">Merge customers</h4>
              <p className="text-xs text-muted-foreground">Aggregates every payer/lead across 14 sources, dedupes by email, and upserts to HubSpot contacts.</p>
            </div>
            <Button size="sm" onClick={() => runCustomers.mutate()} disabled={runCustomers.isPending}>
              {runCustomers.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
              Run merge
            </Button>
          </div>
          {customerReport?.reconciliation && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Stat label="Raw rows scanned" value={customerReport.reconciliation.raw_rows_scanned} />
              <Stat label="Valid records" value={customerReport.reconciliation.valid_records} />
              <Stat label="Unique contacts" value={customerReport.reconciliation.unique_contacts} />
              <Stat label="Duplicates deduped" value={customerReport.reconciliation.dedup_saved} />
              <Stat label="Multi-source contacts" value={customerReport.reconciliation.multi_source_contacts} />
              <Stat label="Enriched (name/phone/co.)" value={customerReport.reconciliation.enriched_contacts} />
              <Stat label="Invalid / missing email" value={customerReport.reconciliation.invalid_or_missing_email} />
              <Stat label="Upserted (new + updated)" value={(customerReport.created ?? 0) + (customerReport.updated ?? 0)} />
              <Stat label="Failed upserts" value={customerReport.reconciliation.duplicates_failed} tone={customerReport.reconciliation.duplicates_failed > 0 ? "bad" : "ok"} />
            </div>
          )}
          {customerReport?.reconciliation?.source_counts && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-muted-foreground">Per-source breakdown</summary>
              <ul className="mt-1 grid grid-cols-2 gap-x-4">
                {Object.entries(customerReport.reconciliation.source_counts as Record<string, number>).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></li>
                ))}
              </ul>
            </details>
          )}
          {customerReport?.errors?.length ? (
            <div className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700">
              <div className="font-semibold">Errors:</div>
              <ul className="list-disc pl-4">{customerReport.errors.map((e: string, i: number) => <li key={i}>{e}</li>)}</ul>
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-navy">Brand → company mapper</h4>
              <p className="text-xs text-muted-foreground">Creates or updates a HubSpot company for each <code>client_brand</code> and associates matching contacts by domain.</p>
            </div>
            <Button size="sm" onClick={() => runBrands.mutate()} disabled={runBrands.isPending}>
              {runBrands.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Briefcase className="mr-1 h-4 w-4" />}
              Map brands
            </Button>
          </div>
          {brandReport && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Stat label="Brands scanned" value={brandReport.brands_scanned} />
              <Stat label="Companies created" value={brandReport.companies_created} />
              <Stat label="Companies updated" value={brandReport.companies_updated} />
              <Stat label="Contacts linked" value={brandReport.contacts_linked} />
              <Stat label="Contacts created" value={brandReport.contacts_created} />
              <Stat label="Failed brands" value={brandReport.failed} tone={brandReport.failed > 0 ? "bad" : "ok"} />
            </div>
          )}
          {brandReport?.per_brand?.length ? (
            <div className="mt-3 max-h-64 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left"><tr>
                  <th className="p-2">Brand</th><th className="p-2">Domain</th><th className="p-2">Company ID</th><th className="p-2">Linked</th><th className="p-2">Status</th>
                </tr></thead>
                <tbody>
                  {brandReport.per_brand.map((b: any) => (
                    <tr key={b.brand_id} className="border-t border-border">
                      <td className="p-2">{b.brand_name}</td>
                      <td className="p-2 text-muted-foreground">{b.domain ?? "—"}</td>
                      <td className="p-2 font-mono">{b.company_id ?? "—"}</td>
                      <td className="p-2">{b.linked_contacts}</td>
                      <td className="p-2">{b.status === "ok" ? <Badge variant="secondary">ok</Badge> : <Badge variant="destructive" title={b.error}>failed</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Tip: run <b>Merge customers</b> first so HubSpot contacts exist, then <b>Map brands</b> to link them to company records.</p>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "bad" }) {
  return (
    <div className={`rounded border p-2 ${tone === "bad" ? "border-red-200 bg-red-50" : "border-border bg-muted/30"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-navy">{Number(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verification panel — every accounting record → HubSpot contact/company link
// ---------------------------------------------------------------------------
function VerificationPanel() {
  const verifyFn = useServerFn(verifyAccountingCrmLinks);
  const [report, setReport] = useState<any | null>(null);
  const [filter, setFilter] = useState<"all" | "no_email" | "no_contact" | "no_company">("all");

  const run = useMutation({
    mutationFn: () => verifyFn({ data: {} }),
    onSuccess: (r: any) => { setReport(r); toast.success(`Verified ${r.counts.total} records`); },
    onError: (e: any) => toast.error(e?.message ?? "Verification failed"),
  });

  const rows = (report?.unresolved ?? []).filter((r: any) => filter === "all" ? true : r.status === filter);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-semibold text-navy">Verify accounting ↔ CRM links</h4>
            <p className="text-xs text-muted-foreground">Scans every paid order, invoice, subscription and installment plan and confirms it resolves to a HubSpot contact and (where applicable) company.</p>
          </div>
          <Button size="sm" onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Target className="mr-1 h-4 w-4" />}
            Run verification
          </Button>
        </div>
        {report?.counts && (
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4 text-xs">
            <Stat label="Total records" value={report.counts.total} />
            <Stat label="Linked ✓" value={report.counts.ok} />
            <Stat label="Missing contact" value={report.counts.no_contact} tone={report.counts.no_contact > 0 ? "bad" : "ok"} />
            <Stat label="Missing company" value={report.counts.no_company} tone={report.counts.no_company > 0 ? "bad" : "ok"} />
            <Stat label="No email on record" value={report.counts.no_email} tone={report.counts.no_email > 0 ? "bad" : "ok"} />
            <Stat label="Unique emails" value={report.counts.unique_emails} />
            <Stat label="Matched contacts" value={report.counts.matched_contacts} />
          </div>
        )}
      </div>

      {report && (
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between gap-2 border-b border-border p-3">
            <div className="text-sm font-medium">Unresolved records ({report.unresolved.length})</div>
            <div className="flex gap-1 text-xs">
              {(["all", "no_contact", "no_company", "no_email"] as const).map((f) => (
                <button key={f} onClick={() => setFilter(f)} className={`rounded px-2 py-1 ${filter === f ? "bg-navy text-white" : "bg-muted text-muted-foreground"}`}>{f.replace("_", " ")}</button>
              ))}
            </div>
          </div>
          <div className="max-h-[500px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/60 text-left">
                <tr>
                  <th className="p-2">Source</th><th className="p-2">Reference</th><th className="p-2">Email</th>
                  <th className="p-2">Contact ID</th><th className="p-2">Company IDs</th><th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No records match this filter.</td></tr>
                ) : rows.map((r: any, i: number) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-2"><Badge variant="outline">{r.source}</Badge></td>
                    <td className="p-2 font-mono">{r.reference}</td>
                    <td className="p-2">{r.email ?? <span className="text-red-600">—</span>}</td>
                    <td className="p-2 font-mono">{r.contact_id ?? "—"}</td>
                    <td className="p-2 font-mono">{(r.company_ids ?? []).join(", ") || "—"}</td>
                    <td className="p-2">
                      <Badge variant={r.status === "ok" ? "secondary" : "destructive"}>{r.status.replace("_", " ")}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {report.counts.no_contact > 0 && (
            <div className="border-t border-border bg-amber-50 p-3 text-xs text-amber-800">
              <b>Fix:</b> switch to <b>Reconciliation → Merge customers</b> to create the missing HubSpot contacts, then re-run verification.
            </div>
          )}
          {report.counts.no_company > 0 && report.counts.no_contact === 0 && (
            <div className="border-t border-border bg-amber-50 p-3 text-xs text-amber-800">
              <b>Fix:</b> switch to <b>Reconciliation → Map brands</b> to link contacts to their brand company.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Contacts sub-panel: search, filters, pagination, CSV export, add/update
// ---------------------------------------------------------------------------
type ContactFilters = { query: string; lifecyclestage: string; lead_status: string };

function ContactsPanel() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ContactFilters>({ query: "", lifecyclestage: "", lead_status: "" });
  const [pageStack, setPageStack] = useState<(string | undefined)[]>([undefined]); // cursors per page (index 0 = first)
  const [pageIdx, setPageIdx] = useState(0);
  const [openContact, setOpenContact] = useState<any | null>(null);
  const [form, setForm] = useState({ email: "", firstname: "", lastname: "", phone: "", company: "" });

  const listFn = useServerFn(listHubspotContacts);
  const upsertFn = useServerFn(upsertHubspotContact);

  const contacts = useQuery({
    queryKey: ["hubspot-contacts", filters, pageStack[pageIdx] ?? "0"],
    queryFn: () => listFn({ data: { ...filters, query: filters.query || undefined, lifecyclestage: filters.lifecyclestage || undefined, lead_status: filters.lead_status || undefined, after: pageStack[pageIdx], limit: 25 } }),
    staleTime: 30_000,
  });

  const applyFilters = () => {
    setFilters((f) => ({ ...f, query: search.trim() }));
    setPageStack([undefined]);
    setPageIdx(0);
  };

  const clearAll = () => {
    setSearch("");
    setFilters({ query: "", lifecyclestage: "", lead_status: "" });
    setPageStack([undefined]);
    setPageIdx(0);
  };

  const nextCursor: string | undefined = (contacts.data as any)?.paging?.next?.after;
  const goNext = () => {
    if (!nextCursor) return;
    const nextStack = [...pageStack];
    if (nextStack.length <= pageIdx + 1) nextStack.push(nextCursor);
    else nextStack[pageIdx + 1] = nextCursor;
    setPageStack(nextStack);
    setPageIdx(pageIdx + 1);
  };
  const goPrev = () => setPageIdx(Math.max(0, pageIdx - 1));

  const upsert = useMutation({
    mutationFn: (payload: any) => upsertFn({ data: payload }),
    onSuccess: (r: any) => {
      toast.success(r.created ? "Contact created in HubSpot" : "Contact updated in HubSpot");
      setForm({ email: "", firstname: "", lastname: "", phone: "", company: "" });
      contacts.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save contact"),
  });

  async function exportCsv() {
    try {
      toast.info("Preparing CSV…");
      const full: any = await listFn({ data: { ...filters, query: filters.query || undefined, lifecyclestage: filters.lifecyclestage || undefined, lead_status: filters.lead_status || undefined, all: true } });
      const rows: any[] = full?.results ?? [];
      if (!rows.length) return toast.error("No contacts to export");
      const header = ["First name", "Last name", "Email", "Phone", "Company", "Job title", "City", "Lifecycle stage", "Lead status", "Created"];
      const esc = (s: any) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      const lines = [header.join(",")];
      for (const c of rows) {
        const p = c.properties ?? {};
        lines.push([
          p.firstname, p.lastname, p.email, p.phone, p.company, p.jobtitle, p.city,
          p.lifecyclestage, p.hs_lead_status, p.createdate ? new Date(p.createdate).toISOString().slice(0, 10) : "",
        ].map(esc).join(","));
      }
      const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hubspot-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} contacts`);
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    }
  }

  const results: any[] = (contacts.data as any)?.results ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="grid gap-1">
            <Label className="text-xs">Search</Label>
            <Input
              placeholder="Name, email, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="h-9 w-[240px]"
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Lifecycle stage</Label>
            <select
              value={filters.lifecyclestage}
              onChange={(e) => { setFilters((f) => ({ ...f, lifecyclestage: e.target.value })); setPageStack([undefined]); setPageIdx(0); }}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All stages</option>
              <option value="subscriber">Subscriber</option>
              <option value="lead">Lead</option>
              <option value="marketingqualifiedlead">MQL</option>
              <option value="salesqualifiedlead">SQL</option>
              <option value="opportunity">Opportunity</option>
              <option value="customer">Customer</option>
              <option value="evangelist">Evangelist</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Lead status</Label>
            <select
              value={filters.lead_status}
              onChange={(e) => { setFilters((f) => ({ ...f, lead_status: e.target.value })); setPageStack([undefined]); setPageIdx(0); }}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">Any</option>
              <option value="NEW">New</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="CONNECTED">Connected</option>
              <option value="OPEN_DEAL">Open deal</option>
              <option value="UNQUALIFIED">Unqualified</option>
            </select>
          </div>
          <Button size="sm" variant="outline" onClick={applyFilters}>Apply</Button>
          <Button size="sm" variant="ghost" onClick={clearAll}>Clear</Button>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={exportCsv}>
              <FileSpreadsheet className="mr-1 h-4 w-4" /> CSV
            </Button>
          </div>
        </div>

        {contacts.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading contacts…</div>
        ) : contacts.isError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {(contacts.error as any)?.message ?? "Failed to load HubSpot"}
          </div>
        ) : results.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No contacts match. Try clearing filters or click <b>Merge all customers &amp; brands</b> above to pull them from invoices, subscriptions, brand plans, bookings and enquiries.
          </p>
        ) : (
          <>
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/70 text-left text-xs uppercase">
                  <tr>
                    <th className="px-2 py-1">Name</th>
                    <th className="px-2 py-1">Email</th>
                    <th className="px-2 py-1">Phone</th>
                    <th className="px-2 py-1">Company</th>
                    <th className="px-2 py-1">Stage</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((c: any) => {
                    const p = c.properties ?? {};
                    const name = [p.firstname, p.lastname].filter(Boolean).join(" ") || "—";
                    return (
                      <tr key={c.id} className="cursor-pointer border-t border-border hover:bg-muted/50" onClick={() => setOpenContact(c)}>
                        <td className="px-2 py-1 font-medium text-navy">{name}</td>
                        <td className="px-2 py-1 text-xs">{p.email ?? "—"}</td>
                        <td className="px-2 py-1 text-xs">{p.phone ?? "—"}</td>
                        <td className="px-2 py-1 text-xs">{p.company ?? "—"}</td>
                        <td className="px-2 py-1 text-xs capitalize">{p.lifecyclestage ?? "—"}</td>
                        <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <OpenDealButton
                              size="xs"
                              defaults={{
                                dealname: `${name === "—" ? p.email : name} · new deal`,
                                contact_email: p.email,
                                contact_id: c.id,
                              }}
                            />
                            <a
                              href={`https://app.hubspot.com/contacts/_/contact/${c.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-navy underline"
                            >
                              <ExternalLink className="h-3 w-3" /> HubSpot
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>Page {pageIdx + 1} · showing {results.length}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={goPrev} disabled={pageIdx === 0}>
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <Button size="sm" variant="outline" onClick={goNext} disabled={!nextCursor}>
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h4 className="mb-3 flex items-center gap-2 font-display text-base text-navy">
          <UserPlus className="h-4 w-4" /> Add / update contact
        </h4>
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.email) return toast.error("Email is required");
            upsert.mutate({ ...form, lifecyclestage: "lead" });
          }}
        >
          <div className="grid gap-1"><Label className="text-xs">Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1"><Label className="text-xs">First name</Label><Input value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} className="h-9" /></div>
            <div className="grid gap-1"><Label className="text-xs">Last name</Label><Input value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} className="h-9" /></div>
          </div>
          <div className="grid gap-1"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9" /></div>
          <div className="grid gap-1"><Label className="text-xs">Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-9" /></div>
          <Button type="submit" size="sm" className="w-full" disabled={upsert.isPending}>
            {upsert.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1 h-4 w-4" />}
            Save to HubSpot
          </Button>
        </form>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Click any contact row to open the full detail drawer with phone, email, notes and recent ledger activity.
        </p>
      </div>

      <ContactDrawer contact={openContact} onClose={() => setOpenContact(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact detail drawer
// ---------------------------------------------------------------------------
function ContactDrawer({ contact, onClose }: { contact: any | null; onClose: () => void }) {
  const getFn = useServerFn(getHubspotContact);
  const addNoteFn = useServerFn(addHubspotContactNote);
  const activityFn = useServerFn(getContactLedgerActivity);
  const [noteBody, setNoteBody] = useState("");

  const id = contact?.id as string | undefined;
  const email = contact?.properties?.email as string | undefined;

  const detail = useQuery({
    queryKey: ["hubspot-contact-detail", id],
    enabled: Boolean(id),
    queryFn: () => getFn({ data: { id } }),
    staleTime: 15_000,
  });

  const activity = useQuery({
    queryKey: ["hubspot-contact-activity", email],
    enabled: Boolean(email),
    queryFn: () => activityFn({ data: { email: email! } }),
    staleTime: 30_000,
  });

  const addNote = useMutation({
    mutationFn: (body: string) => addNoteFn({ data: { contact_id: id!, body } }),
    onSuccess: () => {
      toast.success("Note added");
      setNoteBody("");
      detail.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to add note"),
  });

  const p = detail.data?.contact?.properties ?? contact?.properties ?? {};
  const name = [p.firstname, p.lastname].filter(Boolean).join(" ") || p.email || "Contact";

  return (
    <Sheet open={Boolean(contact)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-display text-navy">{name}</SheetTitle>
          <SheetDescription className="text-xs">
            {p.email ?? "—"} · {p.lifecyclestage ?? "—"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="grid gap-2 rounded-md border border-border p-3 text-sm">
            <Row label="Email" value={p.email} />
            <Row label="Phone" value={p.phone} />
            <Row label="Company" value={p.company} />
            <Row label="Job title" value={p.jobtitle} />
            <Row label="City" value={p.city} />
            <Row label="Lead status" value={p.hs_lead_status} />
            <Row label="Created" value={p.createdate ? new Date(p.createdate).toLocaleDateString("en-ZA") : "—"} />
          </div>

          <div className="flex gap-2">
            {id ? (
              <>
                <a
                  href={`https://app.hubspot.com/contacts/_/contact/${id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex h-9 items-center gap-1 rounded-md border border-input px-3 text-xs font-medium hover:bg-muted"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open in HubSpot
                </a>
                <OpenDealButton
                  defaults={{
                    dealname: `${name} · new deal`,
                    contact_email: p.email,
                    contact_id: id,
                  }}
                />
              </>
            ) : null}
          </div>

          <div>
            <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-navy">
              <StickyNote className="h-4 w-4" /> Notes
            </h4>
            <Textarea
              rows={3}
              placeholder="Add a note (visible in HubSpot)…"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
            />
            <div className="mt-1 flex justify-end">
              <Button size="sm" disabled={!noteBody.trim() || addNote.isPending} onClick={() => addNote.mutate(noteBody.trim())}>
                {addNote.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save note
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {(detail.data?.notes ?? []).map((n: any) => (
                <div key={n.id} className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                  <div className="text-[10px] text-muted-foreground">{n.created_at ? new Date(n.created_at).toLocaleString("en-ZA") : ""}</div>
                  <div className="whitespace-pre-wrap">{htmlToPlainText(n.body ?? "")}</div>
                </div>
              ))}
              {detail.data && (detail.data.notes ?? []).length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No notes yet.</p>
              ) : null}
            </div>
          </div>

          <div>
            <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-navy">
              <FileText className="h-4 w-4" /> Recent ledger activity
            </h4>
            {activity.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div>
            ) : (activity.data?.rows ?? []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground">No ledger records tied to this email.</p>
            ) : (
              <div className="space-y-1">
                <div className="text-[11px] text-muted-foreground">
                  Lifetime linked: <b className="text-navy">{rZAR(activity.data?.total_cents ?? 0)}</b> across {activity.data?.count} records
                </div>
                <div className="max-h-64 overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <tbody>
                      {(activity.data?.rows ?? []).map((r: any, i: number) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1 text-[10px] text-muted-foreground">{r.at ? new Date(r.at).toLocaleDateString("en-ZA") : "—"}</td>
                          <td className="px-2 py-1"><Badge variant="outline" className="text-[10px]">{r.source}</Badge></td>
                          <td className="px-2 py-1">{r.description}</td>
                          <td className="px-2 py-1 text-right font-medium">{rZAR(r.amount_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {(detail.data?.deals ?? []).length > 0 ? (
            <div>
              <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-navy">
                <Briefcase className="h-4 w-4" /> Deals
              </h4>
              <div className="space-y-1">
                {(detail.data?.deals ?? []).map((d: any) => (
                  <div key={d.id} className="flex items-center justify-between rounded-md border border-border p-2 text-xs">
                    <div>
                      <div className="font-medium">{d.dealname}</div>
                      <div className="text-[10px] text-muted-foreground">Stage: {d.dealstage} · Close: {d.closedate?.slice(0, 10) ?? "—"}</div>
                    </div>
                    <div className="font-semibold text-navy">R{Number(d.amount ?? 0).toLocaleString("en-ZA")}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-navy">{value || "—"}</span>
    </div>
  );
}

// Render HubSpot note bodies as plain text to prevent XSS from HTML markup or event handlers.
function htmlToPlainText(html: string): string {
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

// ---------------------------------------------------------------------------
// Pipeline (deals) sub-panel
// ---------------------------------------------------------------------------
function PipelinePanel() {
  const dealsFn = useServerFn(listHubspotDeals);
  const pipelinesFn = useServerFn(listHubspotPipelines);
  const updateStageFn = useServerFn(updateHubspotDealStage);

  const pipelines = useQuery({
    queryKey: ["hubspot-pipelines"],
    queryFn: () => pipelinesFn({}),
    staleTime: 60_000,
  });

  const deals = useQuery({
    queryKey: ["hubspot-deals"],
    queryFn: () => dealsFn({ data: { limit: 100 } }),
    staleTime: 30_000,
  });

  const defaultPipeline = (pipelines.data?.results ?? [])[0];
  const stages: any[] = (defaultPipeline?.stages ?? []).sort((a: any, b: any) => a.displayOrder - b.displayOrder);

  const updateStage = useMutation({
    mutationFn: (v: { deal_id: string; dealstage: string }) => updateStageFn({ data: v }),
    onSuccess: () => { toast.success("Stage updated"); deals.refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update stage"),
  });

  const dealsByStage = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const s of stages) map.set(s.id, []);
    for (const d of deals.data?.results ?? []) {
      const s = d.properties?.dealstage;
      if (map.has(s)) map.get(s)!.push(d);
      else map.set(s ?? "unknown", [...(map.get(s ?? "unknown") ?? []), d]);
    }
    return map;
  }, [deals.data, stages]);

  const stageTotal = (list: any[]) => list.reduce((s, d) => s + Number(d.properties?.amount ?? 0), 0);

  if (pipelines.isLoading || deals.isLoading) {
    return <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading pipeline…</div>;
  }
  if (pipelines.isError || deals.isError) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
        {((pipelines.error ?? deals.error) as any)?.message ?? "Failed to load HubSpot deals"}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Pipeline: <b className="text-navy">{defaultPipeline?.label ?? "Default"}</b> · {deals.data?.results?.length ?? 0} open deals
        </div>
        <OpenDealButton defaults={{ dealname: "New deal" }} label="New deal" />
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {stages.map((s: any) => {
          const list = dealsByStage.get(s.id) ?? [];
          return (
            <div key={s.id} className="flex flex-col rounded-xl border border-border bg-muted/30 p-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-navy">{s.label}</span>
                <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
              </div>
              <div className="mb-2 text-[10px] text-muted-foreground">Total: R{stageTotal(list).toLocaleString("en-ZA")}</div>
              <div className="flex-1 space-y-2">
                {list.length === 0 ? (
                  <p className="text-[11px] italic text-muted-foreground">No deals.</p>
                ) : list.map((d: any) => (
                  <div key={d.id} className="rounded-md border border-border bg-card p-2 text-xs shadow-sm">
                    <div className="font-medium text-navy">{d.properties?.dealname}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">R{Number(d.properties?.amount ?? 0).toLocaleString("en-ZA")} · close {d.properties?.closedate?.slice(0, 10) ?? "—"}</div>
                    <div className="mt-1 flex items-center gap-1">
                      <select
                        className="h-6 flex-1 rounded border border-input bg-background px-1 text-[10px]"
                        value={d.properties?.dealstage ?? ""}
                        onChange={(e) => updateStage.mutate({ deal_id: d.id, dealstage: e.target.value })}
                      >
                        {stages.map((s2: any) => <option key={s2.id} value={s2.id}>{s2.label}</option>)}
                      </select>
                      <a
                        href={`https://app.hubspot.com/contacts/_/deal/${d.id}`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-navy underline"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Open-deal button + dialog (reused across invoices / subscriptions / contacts)
// ---------------------------------------------------------------------------
type DealDefaults = {
  dealname: string;
  amount?: number;
  contact_email?: string;
  contact_id?: string;
  source_reference?: string;
};

function OpenDealButton({
  defaults,
  size = "sm",
  variant = "outline",
  label = "→ Deal",
}: { defaults: DealDefaults; size?: "sm" | "xs"; variant?: "outline" | "ghost" | "default"; label?: string }) {
  const [open, setOpen] = useState(false);
  const btnSize = size === "xs" ? "h-6 px-2 text-[10px]" : "";
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={variant as any}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={btnSize}
      >
        <Target className={size === "xs" ? "mr-0.5 h-3 w-3" : "mr-1 h-3.5 w-3.5"} />
        {label}
      </Button>
      {open ? <OpenDealDialog defaults={defaults} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function OpenDealDialog({ defaults, onClose }: { defaults: DealDefaults; onClose: () => void }) {
  const [form, setForm] = useState({
    dealname: defaults.dealname,
    amount: defaults.amount != null ? String(defaults.amount) : "",
    contact_email: defaults.contact_email ?? "",
    dealstage: "",
    pipeline: "",
    closedate: "",
  });
  const createFn = useServerFn(createHubspotDeal);
  const pipelinesFn = useServerFn(listHubspotPipelines);
  const pipelines = useQuery({
    queryKey: ["hubspot-pipelines-dialog"],
    queryFn: () => pipelinesFn({}),
    staleTime: 60_000,
  });

  const pipeline = (pipelines.data?.results ?? [])[0];
  const stages: any[] = (pipeline?.stages ?? []).sort((a: any, b: any) => a.displayOrder - b.displayOrder);

  const create = useMutation({
    mutationFn: () => createFn({
      data: {
        dealname: form.dealname,
        amount: form.amount ? Number(form.amount) : undefined,
        contact_email: form.contact_email || undefined,
        contact_id: defaults.contact_id,
        dealstage: form.dealstage || stages[0]?.id,
        pipeline: form.pipeline || pipeline?.id,
        closedate: form.closedate || undefined,
        source_reference: defaults.source_reference,
      },
    }),
    onSuccess: () => {
      toast.success("Deal created in HubSpot");
      onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create deal"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Open new deal</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid gap-1"><Label className="text-xs">Deal name *</Label>
            <Input value={form.dealname} onChange={(e) => setForm({ ...form, dealname: e.target.value })} className="h-9" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1"><Label className="text-xs">Amount (ZAR)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" />
            </div>
            <div className="grid gap-1"><Label className="text-xs">Close date</Label>
              <Input type="date" value={form.closedate} onChange={(e) => setForm({ ...form, closedate: e.target.value })} className="h-9" />
            </div>
          </div>
          <div className="grid gap-1"><Label className="text-xs">Contact email</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="h-9" placeholder="customer@example.com" />
            {defaults.contact_id ? <p className="text-[10px] text-muted-foreground">Will link to existing contact.</p> : null}
          </div>
          <div className="grid gap-1"><Label className="text-xs">Stage</Label>
            <select
              value={form.dealstage}
              onChange={(e) => setForm({ ...form, dealstage: e.target.value })}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">{stages[0]?.label ? `Default (${stages[0].label})` : "Default first stage"}</option>
              {stages.map((s: any) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          {defaults.source_reference ? (
            <p className="text-[11px] text-muted-foreground">Linked to ledger record <b>{defaults.source_reference}</b> — a note will be added on the deal.</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!form.dealname || create.isPending}>
            {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Briefcase className="mr-1 h-4 w-4" />}
            Create deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


